import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { Button } from '../../ui/Button';
import {
  HeadingAnchorPlugin,
  HeadingNumberingPlugin,
  buildEditorStateFromTemplate,
  migrateLegacyVisualPlaceholders,
  reportDraftNodes,
  reportDraftTheme,
} from '../drafting/ReportDraftEditor';
import { ReportPagedCanvas } from '../drafting/pagination/ReportPagedCanvas';
import { BRAND } from '../../../config/brand';
import '../drafting/ReportDraftEditor.css';
import './ReportDraftPreview.css';

const resolvePagedHeaderLabel = (templateDocument, fallbackLabel) => {
  const coverBlock = (templateDocument?.sections || [])
    .flatMap(section => section?.blocks || [])
    .find(block => block?.type === 'cover');
  const titleCandidate = String(
    coverBlock?.title
    || coverBlock?.lines?.[0]
    || fallbackLabel
    || ''
  ).trim();
  return titleCandidate || BRAND.reportTitle;
};

export function ReportDraftPreview({
  serializedState,
  templateDocument,
  onBackToDrafting,
  exportProfile,
  previewMode,
  pdfChromeMetadata,
  paginationMode,
  paginationProfile,
  layoutMode,
}) {
  const { t } = useTranslation();
  const previewContentRef = useRef(null);
  const pagedHeaderLabel = useMemo(
    () => resolvePagedHeaderLabel(templateDocument, t('reports.review.template.cover.reportTitle')),
    [templateDocument, t]
  );
  const placeholderLabel = t('reports.review.template.placeholders.caption');
  const hasTemplate = Boolean(templateDocument?.sections?.length);
  const migratedSerializedState = useMemo(
    () => migrateLegacyVisualPlaceholders(serializedState, templateDocument),
    [serializedState, templateDocument]
  );
  const hasDraftState = Boolean(migratedSerializedState);
  const shouldShowEmptyState = !hasDraftState && !hasTemplate;
  const isPdfExactMode = previewMode === 'pdf_exact';

  const initialConfig = useMemo(
    () => ({
      namespace: 'AgriSenseReportPreview',
      editable: false,
      theme: reportDraftTheme,
      nodes: reportDraftNodes,
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      editorState: hasDraftState
        ? migratedSerializedState
        : () => buildEditorStateFromTemplate(templateDocument, placeholderLabel),
    }),
    [hasDraftState, migratedSerializedState, templateDocument, placeholderLabel]
  );

  if (shouldShowEmptyState) {
    return (
      <div className="report-draft-preview report-draft-preview--empty">
        <div className="report-draft-preview__empty">
          <h3 className="report-draft-preview__empty-title">
            {t('reports.previewPage.emptyState.title')}
          </h3>
          <p className="report-draft-preview__empty-description">
            {t('reports.previewPage.emptyState.description')}
          </p>
          {typeof onBackToDrafting === 'function' && (
            <Button variant="secondary" size="md" onClick={onBackToDrafting}>
              {t('reports.previewPage.backToDrafting')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`report-draft-preview report-draft-editor${isPdfExactMode ? ' report-draft-preview--pdf-exact' : ''}`}
      data-report-export-profile={exportProfile || 'default'}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <div className="report-draft-editor__frame report-draft-preview__frame">
          <ReportPagedCanvas
            contentRef={previewContentRef}
            readOnly
            showPageIndicator
            paginationMode={paginationMode}
            paginationProfile={paginationProfile}
            headerLabel={pagedHeaderLabel}
            sheetChromeVariant={isPdfExactMode ? 'pdf' : 'default'}
            sheetHeaderLabels={isPdfExactMode ? {
              left: pdfChromeMetadata?.headerLeft || '',
              right: pdfChromeMetadata?.headerRight || '',
            } : null}
            sheetFooterLabels={isPdfExactMode ? {
              page: pdfChromeMetadata?.pageLabel || 'Pag.',
              of: pdfChromeMetadata?.ofLabel || 'di',
            } : null}
            layoutMode={layoutMode}
            className="report-draft-preview__canvas"
          >
            <div className="report-draft-editor__page report-draft-preview__page">
              <RichTextPlugin
                contentEditable={(
                  <ContentEditable
                    className="report-draft-editor__contenteditable report-draft-preview__contenteditable"
                    contentEditable={false}
                    tabIndex={-1}
                    ref={previewContentRef}
                  />
                )}
                placeholder={null}
                ErrorBoundary={LexicalErrorBoundary}
              />
              <ListPlugin />
              <LinkPlugin />
              <TablePlugin />
              <HeadingAnchorPlugin />
              <HeadingNumberingPlugin />
            </div>
          </ReportPagedCanvas>
        </div>
      </LexicalComposer>
    </div>
  );
}

ReportDraftPreview.propTypes = {
  serializedState: PropTypes.string,
  templateDocument: PropTypes.shape({
    sections: PropTypes.array,
  }),
  onBackToDrafting: PropTypes.func,
  exportProfile: PropTypes.string,
  previewMode: PropTypes.oneOf(['default', 'pdf_exact']),
  pdfChromeMetadata: PropTypes.shape({
    headerLeft: PropTypes.string,
    headerRight: PropTypes.string,
    pageLabel: PropTypes.string,
    ofLabel: PropTypes.string,
  }),
  paginationMode: PropTypes.oneOf(['strict', 'guides', 'continuous']),
  paginationProfile: PropTypes.oneOf(['screen', 'print']),
  layoutMode: PropTypes.oneOf(['strict', 'guides', 'paged_guides', 'continuous']),
};

ReportDraftPreview.defaultProps = {
  serializedState: null,
  templateDocument: null,
  onBackToDrafting: null,
  exportProfile: null,
  previewMode: 'default',
  pdfChromeMetadata: null,
  paginationMode: 'strict',
  paginationProfile: 'screen',
  layoutMode: null,
};
